'use client'

import React, { useTransition } from 'react'
import { Minus, Plus } from 'lucide-react'
import { useSession } from 'next-auth/react'
import { BagCheck } from 'solar-icons'
import { toast } from 'sonner'
import { getProductStockSellingPrices } from '@/lib/actions/products/get-product-stock-selling-prices'
import { useCart, useSearchParams } from '@/lib/hooks'
import { cn, formatPrice, handleNumberInput } from '@/lib/utils'

import {
  Badge,
  Barcode,
  Button,
  Checkbox,
  Input,
  KeyChip,
  Label,
  LoadingDots,
  RadioGroup,
  RadioGroupItem,
  Separator,
  Thumbnail,
} from '@/components/ui'

interface AddToCartProps extends React.ComponentPropsWithoutRef<'form'> {
  product: Product
}

export const AddToCart = ({ product }: AddToCartProps) => {
  const { data: session }: any = useSession()
  const [isFetchingStockPrices, startStockPricesFetch] = useTransition()
  const { cartId } = useSearchParams()
  const { addToCart } = useCart() as any

  const [qty, setQty] = React.useState(1)
  const [selectedPrice, setSelectedPrice] = React.useState<any>(null)
  const [selectedPromo, setSelectedPromo] = React.useState<any>(null)
  const [sellingPrices, setSellingPrices] = React.useState<any>(null)
  const [addons, setAddons] = React.useState<any>(null)
  const [selectedAddons, setSelectedAddons] = React.useState<any[]>([])
  const [customPrice, setCustomPrice] = React.useState<any>(null)

  const fetchStockSellingPrices = async () => {
    startStockPricesFetch(async () => {
      const stockPrices = await getProductStockSellingPrices(product)
      setAddons(stockPrices?.product?.add_ons)
      setSellingPrices(stockPrices)
      setSelectedPrice(stockPrices?.selling_prices?.[0]?.id)
      setSelectedPromo(stockPrices?.promotion_items?.[0]?.id)
    })
  }

  const handleSubmit = async (data: FormData) => {
    const stock_selling_price_id = Number(data.get('stock-price'))
    const qty = Number(data.get('qty'))
    const promotion_item_id = data.get('selected-promotion')
      ? Number(data.get('selected-promotion'))
      : null
    const customPrice = data.get('customPrice')

    if (Number(customPrice) && Number(customPrice) < 1) {
      toast.error('Custom price must be greater than 0')
      return
    }

    const isCustomPrice = session?.customPrice === '1'
    const customPriceValue = Number(customPrice) ?? null
    const barcode = product?.default_price?.barcode

    const customPricePayload = {
      stock_selling_price_id: customPriceValue ? null : stock_selling_price_id,
      price: customPriceValue ?? null,
      barcode,
      variant_id: product?.variant_id,
      qty,
      promotion_item_id,
      addon_price_ids: selectedAddons,
    }

    const stockSellingPricePayload = {
      stock_selling_price_id,
      promotion_item_id,
      addon_price_ids: selectedAddons,
      qty,
    }

    await addToCart({
      cartId,
      product: customPriceValue ? customPricePayload : stockSellingPricePayload,
    })
  }

  React.useEffect(() => {
    fetchStockSellingPrices()
  }, [])

  return (
    <form
      className="flex min-h-[280px] w-full flex-wrap gap-4 lg:gap-8"
      action={handleSubmit}
    >
      <Thumbnail size="lg" src={product?.image_url} />

      <div className="flex w-full max-w-[396px] flex-col flex-wrap gap-4 pr-4">
        <h2 className="text-balance text-xl font-medium">
          {product?.variant_label ?? product?.title}
        </h2>

        <div className="flex w-full flex-col gap-4">
          <div className="flex w-full flex-col gap-4 md:flex-row">
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="w-[36px] px-0" size="md">
                SKU
              </Badge>
              <span className="font-mono text-sm font-medium text-gray-950">
                {product?.sku}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="min-w-5 px-1" size="md">
                <Barcode className="h-3" />
              </Badge>
              <span className="font-mono text-sm font-medium text-gray-950">
                {product?.default_price?.barcode}
              </span>
            </div>
          </div>
        </div>

        {isFetchingStockPrices && (
          <div className="flex h-full w-full items-center justify-center lg:h-[360px]">
            <LoadingDots />
          </div>
        )}

        {!isFetchingStockPrices && (
          <div className="flex h-auto w-full flex-col gap-5 lg:min-h-[360px]">
            {sellingPrices?.selling_prices?.length > 0 && (
              <div className="flex w-full flex-col gap-2">
                <div className="flex w-full items-center gap-2">
                  <Label className="shrink-0 !text-sm font-medium text-gray-700">
                    Selling Price
                  </Label>
                  <Separator className="mt-1" />
                </div>

                <RadioGroup
                  className="flex w-full flex-col gap-1.5"
                  name="stock-price"
                  value={selectedPrice}
                  onValueChange={(value) => setSelectedPrice(value)}
                >
                  {sellingPrices?.selling_prices?.map(
                    (price: any, index: number) => {
                      return (
                        <div
                          key={price?.id}
                          className={cn(
                            'relative flex w-full cursor-pointer flex-col items-center gap-2 overflow-hidden rounded-md border border-gray-100 shadow-card hover:bg-gray-50 lg:flex-row',
                            {
                              'border-gray-500': price?.id === selectedPrice,
                            },
                          )}
                        >
                          <RadioGroupItem
                            value={price?.id}
                            id={`price-${price?.id}`}
                            className="absolute inset-y-0 right-2 my-auto"
                          />
                          <Label
                            htmlFor={`price-${price?.id}`}
                            className="w-full cursor-auto px-3 py-3 font-mono !text-base font-semibold"
                          >
                            {formatPrice(price?.amount, 2, true)}
                          </Label>

                          {price?.stock?.expiry_date && (
                            <div className="flex shrink-0 flex-col items-end pr-11 text-right">
                              <span className="shrink-0 text-xs text-gray-700">
                                Expires on
                              </span>
                              <span className="shrink-0 text-xs font-medium text-gray-950">
                                {price?.stock?.expiry_date}
                              </span>
                            </div>
                          )}
                        </div>
                      )
                    },
                  )}
                </RadioGroup>
              </div>
            )}

            {session?.customPrice === '1' && (
              <div className="flex w-full flex-col gap-2">
                <div className="flex w-full items-center gap-2">
                  <Label className="shrink-0 !text-sm font-medium text-gray-700">
                    Custom Price
                  </Label>
                  <Separator className="mt-1" />
                </div>
                <div className="flex">
                  <Input
                    className="w-full appearance-none text-center"
                    value={customPrice ? customPrice : ''}
                    inputSize="lg"
                    min={1}
                    name="customPrice"
                    onChangeValue={(value) => {
                      setCustomPrice(value)
                    }}
                    onKeyDown={handleNumberInput}
                    type="number"
                    placeholder="Enter custom price"
                  />
                </div>
              </div>
            )}

            {sellingPrices?.promotion_items?.length > 0 && (
              <div className="flex w-full flex-col gap-2">
                <div className="flex w-full items-center gap-2">
                  <Label className="shrink-0 !text-sm font-medium text-gray-700">
                    Promotions
                  </Label>
                  <Separator className="mt-1" />
                </div>

                <RadioGroup
                  value={selectedPromo}
                  onValueChange={(value) => setSelectedPromo(value)}
                  className="flex w-full flex-col gap-1.5"
                  name="selected-promotion"
                >
                  {sellingPrices?.promotion_items?.map(
                    (promo: any, index: number) => {
                      return (
                        <div
                          key={index}
                          className={cn(
                            'relative flex w-full cursor-pointer flex-col items-center gap-2 overflow-hidden rounded-md border border-gray-100 shadow-card hover:bg-gray-50 lg:flex-row',
                            {
                              'border-gray-500': promo?.id === selectedPromo,
                            },
                          )}
                        >
                          <RadioGroupItem
                            className="absolute inset-y-0 right-2 my-auto"
                            value={promo?.id}
                            id={`promo-${promo?.id}`}
                          />
                          <Label
                            htmlFor={`promo-${promo?.id}`}
                            className="flex w-full cursor-auto items-center justify-between px-3 py-3 pr-10 !text-sm font-semibold"
                          >
                            <span>{promo?.title}</span>
                            <span>
                              {promo?.value}
                              {promo?.type === 'percentage' && '%'}
                            </span>
                          </Label>
                        </div>
                      )
                    },
                  )}
                </RadioGroup>
              </div>
            )}

            {addons?.length > 0 && (
              <div className="flex w-full flex-col gap-2">
                <div className="flex w-full items-center gap-2">
                  <Label className="shrink-0 !text-sm font-medium text-gray-700">
                    Addons
                  </Label>
                  <Separator className="mt-1" />
                </div>

                {addons?.map((addon: any) => {
                  return (
                    <div
                      key={addon?.id}
                      className={cn(
                        'relative flex w-full cursor-pointer flex-col items-center gap-2 overflow-hidden rounded-md border border-gray-100 shadow-card hover:bg-gray-50 lg:flex-row',
                        {
                          'border-gray-500': selectedAddons?.includes(
                            addon?.default_price?.price_id,
                          ),
                        },
                      )}
                    >
                      <Checkbox
                        className="absolute inset-y-0 right-2 my-auto"
                        value={addon?.default_price?.price_id}
                        id={`addon-${addon?.default_price?.price_id}`}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setSelectedAddons([
                              ...selectedAddons,
                              addon?.default_price?.price_id,
                            ])
                          } else {
                            setSelectedAddons(
                              selectedAddons?.filter(
                                (id: any) =>
                                  id !== addon?.default_price?.price_id,
                              ),
                            )
                          }
                        }}
                      />
                      <Label
                        htmlFor={`addon-${addon?.default_price?.price_id}`}
                        className="flex w-full cursor-auto items-center justify-between p-2 pr-10 !text-sm font-semibold"
                      >
                        <div className="flex items-center gap-2">
                          <Thumbnail
                            src={addon?.image_url}
                            className="size-8 rounded-md"
                          />
                          <span>{addon?.name}</span>
                        </div>
                        <span>
                          {formatPrice(addon?.default_price?.price, 2, true)}
                        </span>
                      </Label>
                    </div>
                  )
                })}
              </div>
            )}

            <div className="flex w-full flex-col gap-2">
              <div className="flex w-full items-center gap-2">
                <Label className="shrink-0 !text-sm font-medium text-gray-700">
                  Quantity
                </Label>
                <Separator className="mt-1" />
              </div>

              <div className="flex items-center gap-2">
                <Input
                  className="max-w-[200px] appearance-none text-center"
                  key={qty}
                  value={qty}
                  inputSize="lg"
                  min={1}
                  name="qty"
                  onChangeValue={(value) => {
                    setQty(Number(value))
                  }}
                  onKeyDown={handleNumberInput}
                  type="number"
                />
                <div className="flex w-full items-center gap-2">
                  <Button
                    size="lg"
                    variant="outline"
                    className="w-full"
                    onClick={() => {
                      if (qty > 1) {
                        setQty((prevQty) => prevQty - 1)
                      }
                    }}
                    type="button"
                  >
                    <Minus className="size-5" strokeWidth={1.5} />
                  </Button>
                  <Button
                    size="lg"
                    variant="outline"
                    className="w-full"
                    onClick={() => {
                      setQty((prevQty) => prevQty + 1)
                    }}
                    type="button"
                  >
                    <Plus className="size-5" strokeWidth={1.5} />
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="flex w-full pt-3">
          <Button
            variant="brand"
            className="w-full gap-3 shadow-card"
            size="lg"
            type="submit"
            disabled={isFetchingStockPrices}
          >
            <BagCheck className="size-5" strokeWidth={1.5} />
            <span>Add to Cart</span>
            <KeyChip variant="dark" shortcut="enter" />
          </Button>
        </div>
      </div>
    </form>
  )
}
